const readItems = (response) => response?.items;

/**
 * Every row of an offset-paged list, for a caller with no pagination of its own
 * to fall back on — a dropdown, or a table that filters client-side. A short
 * read there leaves rows missing with no error, so the list is paged to
 * exhaustion rather than truncated.
 *
 * Page 1 comes first and the rest are requested in parallel only if its `total`
 * says there are more, so a list that fits one page costs exactly one request.
 * `maxPage` caps the fan-out for an endpoint that refuses pages past an offset
 * ceiling, so a wrong `total` cannot walk the read off the end of it.
 *
 * Rows are de-duplicated by `id`, first occurrence kept: concurrent pages of an
 * offset-paginated list can overlap if a row is inserted mid-read, and a
 * duplicate id is a duplicate React key in whatever renders it. Rows with no id
 * are passed through for the caller's adapter to judge.
 *
 * @param {(params: { page: number, limit: number }) => Promise<Object>} fetchPage
 *   One page of the list, by 1-based index.
 * @param {Object} options
 * @param {number} options.limit - The page size to ask for.
 * @param {number} [options.maxPage=Infinity] - The last page the endpoint serves.
 * @param {(response: Object) => Object[]} [options.getItems] - Reads a page's
 *   rows from its envelope; defaults to `items`.
 * @returns {Promise<Object[]>} The raw rows, in server order.
 */
export const fetchAllPages = async (
  fetchPage,
  { limit, maxPage = Infinity, getItems = readItems },
) => {
  const firstPage = await fetchPage({ page: 1, limit });

  const rows = [...(getItems(firstPage) ?? [])];
  const total = firstPage?.total ?? rows.length;
  // Some envelopes echo the page size the server applied; fall back to what we
  // asked for rather than dividing by zero if one omits or zeroes the field.
  const appliedLimit = firstPage?.limit || limit;
  const pageCount = Math.min(Math.ceil(total / appliedLimit), maxPage);

  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      fetchPage({ page: index + 2, limit }),
    ),
  );
  remainingPages.forEach((page) => rows.push(...(getItems(page) ?? [])));

  const seenIds = new Set();

  return rows.filter((row) => {
    if (row?.id == null) return true;
    if (seenIds.has(row.id)) return false;
    seenIds.add(row.id);

    return true;
  });
};
